import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ROLE_LABELS } from './rbac';

const EMPTY = 'Non renseigné';

const KYC_STATUS_LABELS = {
  NONE: 'Aucun',
  PENDING: 'En attente',
  VERIFIED: 'Validé',
  REJECTED: 'Rejeté',
};

const VENDOR_STATUS_LABELS = {
  PENDING: 'En attente',
  APPROVED: 'Approuvé',
  REJECTED: 'Rejeté',
};

const HOST_STATUS_LABELS = {
  '0': 'Non hôte',
  '1': 'Hôte actif',
  '2': 'Demande en attente',
};

const LOGIN_TYPE_LABELS = {
  email: 'E-mail',
  google: 'Google',
  apple: 'Apple',
};

const display = (value) => {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'string' && value.trim() === '') return EMPTY;
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'object' && value !== null) {
    if (value.fullName || value.email) {
      return display(value.fullName || value.email);
    }
    return EMPTY;
  }
  return String(value);
};

const formatDate = (dateString) => {
  if (!dateString) return EMPTY;
  const d = dateString instanceof Date ? dateString : new Date(dateString);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const slugifyFilename = (name) =>
  String(name || 'utilisateur')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
    .toLowerCase() || 'utilisateur';

const BRAND_RGB = [39, 72, 158];

const addSectionTitle = (doc, y, title) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - 30) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_RGB);
  doc.text(title, 14, y);
  doc.setDrawColor(220, 220, 220);
  doc.line(14, y + 1.5, 196, y + 1.5);
  doc.setTextColor(0, 0, 0);
  return y + 7;
};

const addFieldTable = (doc, startY, rows) => {
  if (!rows.length) return startY;
  autoTable(doc, {
    startY,
    head: [['Champ', 'Valeur']],
    body: rows.map(([label, value]) => [label, display(value)]),
    theme: 'striped',
    headStyles: { fillColor: BRAND_RGB, fontSize: 9 },
    styles: { fontSize: 8.5, cellPadding: 2.5, overflow: 'linebreak' },
    columnStyles: {
      0: { cellWidth: 52, fontStyle: 'bold' },
      1: { cellWidth: 'auto' },
    },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 5;
};

const bookingRows = (bookings, mode) => {
  if (!bookings?.length) return [];
  return bookings.map((b) => {
    const vehicle =
      b.vehicle?._id?.title || b.vehicle?.title || 'Véhicule';
    const dates = `${formatDate(b.dates?.start)} → ${formatDate(b.dates?.end)}`;
    const amount = `${b.financials?.total ?? 0} ${b.financials?.currency || 'MAD'}`;
    const party =
      mode === 'client'
        ? display(
            b.vendor?.vendorDetails?.companyName ||
              b.vendor?.fullName ||
              b.vendor?.email
          )
        : display(b.client?.fullName || b.client?.email);
    return [vehicle, dates, amount, b.status || '—', party];
  });
};

/**
 * Génère et télécharge un PDF récapitulatif pour un utilisateur admin.
 * @param {{ user, bookings?, stats?, vendorBookings?, vendorStats? }} detailsData
 */
export function exportUserDetailsToPdf(detailsData) {
  const user = detailsData?.user;
  if (!user) {
    throw new Error('Aucune donnée utilisateur à exporter');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const exportedAt = formatDate(new Date());

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...BRAND_RGB);
  doc.text('Rapport Utilisateur — Carvy', 14, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(`Date d'export : ${exportedAt}`, 14, 26);
  doc.setTextColor(0, 0, 0);
  doc.text(`Utilisateur : ${display(user.fullName)}`, 14, 32);
  doc.text(`E-mail : ${display(user.email)}`, 14, 38);
  doc.text(`Identifiant : ${display(user._id)}`, 14, 44);

  let y = 52;
  const ud = user.userDetails;
  const kyc = user.kycDocuments;
  const vd = user.vendorDetails;
  const birthdateDisplay =
    user.birthdate != null
      ? formatDate(user.birthdate)
      : ud?.birthdate
        ? display(ud.birthdate)
        : EMPTY;

  y = addSectionTitle(doc, y, '1. Profil & identité');
  y = addFieldTable(doc, y, [
    ['Nom complet', user.fullName || [user.firstName, user.middle, user.lastName].filter(Boolean).join(' ')],
    ['Prénom', user.firstName],
    ['Nom', user.lastName],
    ['Deuxième prénom', user.middle],
    ['E-mail', user.email],
    ['E-mail vérifié', user.emailVerified],
    ['Date de validation e-mail', formatDate(user.emailVerifiedAt || user.email_verified_at)],
    ['Téléphone', user.phoneNumber ? `${user.countryCode || '+212'} ${user.phoneNumber}` : null],
    ['Téléphone vérifié', user.phoneVerified],
    ['Date de validation téléphone', formatDate(user.phoneVerifiedAt || user.phone_verified_at)],
    ['Date de naissance', birthdateDisplay],
    ['Inscription', formatDate(user.createdAt)],
    ['Dernière mise à jour', formatDate(user.updatedAt)],
    ['IP d\'inscription', user.registration_ip],
    ['Dernière IP de connexion', user.last_login_ip],
  ]);

  y = addSectionTitle(doc, y, '2. Compte & RBAC');
  y = addFieldTable(doc, y, [
    ['Rôle', ROLE_LABELS[user.role] || user.role],
    ['Statut du compte', user.accountStatus === 'SUSPENDED' ? 'Suspendu' : 'Actif'],
    ['Statut KYC', KYC_STATUS_LABELS[user.kycStatus] || user.kycStatus],
    ['KYC vérifié (flag)', user.kycVerified],
    ['CGU acceptées', user.termsAccepted],
    ['Date d\'acceptation des CGU', formatDate(user.termsAcceptedAt || user.terms_accepted_at)],
    ['Version token', user.tokenVersion],
    ['Date de suspension', formatDate(user.suspendedAt)],
    ['Motif de suspension', user.suspendedReason],
    [
      'Suspendu par',
      user.suspendedBy && typeof user.suspendedBy === 'object'
        ? user.suspendedBy.fullName || user.suspendedBy.email
        : user.suspendedBy,
    ],
  ]);

  const permissions = user.effectivePermissions || user.permissions || [];
  y = addSectionTitle(doc, y, 'Permissions effectives');
  if (permissions.length) {
    autoTable(doc, {
      startY: y,
      head: [['Permission']],
      body: permissions.map((p) => [p]),
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: BRAND_RGB },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 5;
  } else {
    doc.setFontSize(9);
    doc.text(EMPTY, 14, y);
    y += 8;
  }

  y = addSectionTitle(doc, y, '3. KYC & documents');
  y = addFieldTable(doc, y, [
    ['Statut KYC', KYC_STATUS_LABELS[user.kycStatus] || user.kycStatus],
    ['Soumis le', formatDate(kyc?.submittedAt)],
    ['Vérifié le', formatDate(kyc?.verifiedAt)],
    ['Réf. permis principal', kyc?.primaryReferenceNumber],
    ['Réf. permis secondaire', kyc?.secondaryReferenceNumber],
    ['URL pièce d\'identité', user.identityImage ? '(voir plateforme)' : null],
    ['URL permis principal recto', kyc?.primaryDriverLicenseFront ? '(voir plateforme)' : null],
    ['URL permis principal verso', kyc?.primaryDriverLicenseBack ? '(voir plateforme)' : null],
    ['URL permis secondaire recto', kyc?.secondaryDriverLicenseFront ? '(voir plateforme)' : null],
    ['URL permis secondaire verso', kyc?.secondaryDriverLicenseBack ? '(voir plateforme)' : null],
  ]);

  y = addSectionTitle(doc, y, '4. Profil client');
  y = addFieldTable(doc, y, [
    ['Permis (texte)', ud?.driverLicense],
    ['Pays', ud?.country],
    ['Pays par défaut (ISO)', ud?.defaultCountry],
    ['Langue', ud?.langauge],
    ['Portefeuille', ud?.wallet],
    ['Présentation', ud?.intro],
    ['Note moyenne', user.rating?.count ? user.rating.average?.toFixed(2) : null],
    ['Nombre d\'avis', user.rating?.count || null],
  ]);

  if (user.wishlist?.length) {
    y = addSectionTitle(doc, y, 'Favoris (wishlist)');
    autoTable(doc, {
      startY: y,
      head: [['Véhicule']],
      body: user.wishlist.map((v) => [
        typeof v === 'object' ? display(v.title || v.licencePlateNumber || v._id) : display(v),
      ]),
      theme: 'striped',
      headStyles: { fillColor: BRAND_RGB },
      styles: { fontSize: 8.5 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  const stats = detailsData.stats;
  if (stats) {
    y = addSectionTitle(doc, y, '5. Activité — statistiques client');
    autoTable(doc, {
      startY: y,
      head: [['Indicateur', 'Valeur']],
      body: [
        ['Total réservations', stats.totalBookings],
        ['Total dépensé (MAD)', Number(stats.totalSpent || 0).toFixed(2)],
        ['Réservations complétées', stats.completedBookings],
        ['Réservations annulées', stats.cancelledBookings],
      ],
      theme: 'grid',
      headStyles: { fillColor: BRAND_RGB },
      styles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  const clientBookingData = bookingRows(detailsData.bookings, 'client');
  y = addSectionTitle(doc, y, 'Réservations récentes (client)');
  if (clientBookingData.length) {
    autoTable(doc, {
      startY: y,
      head: [['Véhicule', 'Période', 'Montant', 'Statut', 'Vendeur']],
      body: clientBookingData,
      theme: 'striped',
      headStyles: { fillColor: BRAND_RGB, fontSize: 8 },
      styles: { fontSize: 7.5, overflow: 'linebreak' },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 5;
  } else {
    doc.setFontSize(9);
    doc.text(EMPTY, 14, y);
    y += 8;
  }

  if (user.role === 'vendor') {
    y = addSectionTitle(doc, y, '6. Agence / vendeur');
    y = addFieldTable(doc, y, [
      ['Statut agence', VENDOR_STATUS_LABELS[user.vendorStatus] || user.vendorStatus],
      ['Statut hôte (app)', HOST_STATUS_LABELS[user.hostStatus] || user.hostStatus],
      ['Raison sociale', vd?.companyName],
      ['Identifiant fiscal', vd?.taxId],
      ['Forme juridique', vd?.legalForm],
      ['Adresse', vd?.address],
    ]);

    const vs = detailsData.vendorStats;
    if (vs) {
      y = addSectionTitle(doc, y, 'Statistiques vendeur');
      autoTable(doc, {
        startY: y,
        head: [['Indicateur', 'Valeur']],
        body: [
          ['Véhicules publiés', vs.vehicleCount],
          ['Total réservations', vs.totalBookings],
          ['Revenu net (MAD)', Number(vs.totalRevenue || 0).toFixed(2)],
          ['En attente', vs.pendingBookings],
          ['Complétées', vs.completedBookings],
          ['Annulées', vs.cancelledBookings],
        ],
        theme: 'grid',
        headStyles: { fillColor: BRAND_RGB },
        styles: { fontSize: 9 },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 5;
    }

    const vendorBookingData = bookingRows(detailsData.vendorBookings, 'vendor');
    y = addSectionTitle(doc, y, 'Réservations récentes (vendeur)');
    if (vendorBookingData.length) {
      autoTable(doc, {
        startY: y,
        head: [['Véhicule', 'Période', 'Montant', 'Statut', 'Client']],
        body: vendorBookingData,
        theme: 'striped',
        headStyles: { fillColor: BRAND_RGB, fontSize: 8 },
        styles: { fontSize: 7.5, overflow: 'linebreak' },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 5;
    }
  }

  y = addSectionTitle(doc, y, '7. Préférences & application');
  addFieldTable(doc, y, [
    ['Type de connexion', LOGIN_TYPE_LABELS[user.loginType] || user.loginType],
    ['Forfait (packageId)', user.packageId],
    [
      'Notifications SMS',
      user.smsNotification === '1' || user.smsNotification === 1 ? 'Activé' : 'Désactivé',
    ],
    [
      'Notifications e-mail',
      user.emailNotification === '1' || user.emailNotification === 1 ? 'Activé' : 'Désactivé',
    ],
    [
      'Notifications push',
      user.pushNotification === '1' || user.pushNotification === 1 ? 'Activé' : 'Désactivé',
    ],
    ['Statut hôte', HOST_STATUS_LABELS[user.hostStatus] || user.hostStatus],
  ]);

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Carvy — Rapport utilisateur — Page ${i}/${pageCount}`,
      14,
      doc.internal.pageSize.getHeight() - 8
    );
  }

  const nameSlug = slugifyFilename(user.fullName || user.email);
  const idShort = String(user._id || '').slice(-8);
  const filename = `client-${nameSlug}-${idShort}.pdf`;
  doc.save(filename);
}

export default exportUserDetailsToPdf;
